/* @sravni/react-header version 0.2.43 */
import { __read, __assign, __spreadArray } from 'tslib';
import * as React from 'react';
import { useBoolean, useDebounceValue, useStyles, useMergedRef } from '@sravni/react-utils';
import { Space } from '@sravni/react-design-system/lib/Space';
import { Icon } from '@sravni/react-design-system/lib/Icon';
import { Col, Row } from '@sravni/react-design-system/lib/Grid';
import { Link, Text } from '@sravni/react-design-system/lib/Typography';
import { Menu } from '@sravni/react-design-system/lib/Menu';
import { Popover } from '@sravni/react-design-system/lib/Popover';
import { getCustomStyles } from '../../../utils/styles.js';
import { getCustomIconStyles } from '../../../utils/customIcons.js';
import { makeRelAttribute } from '../../../utils/links.js';
import { defaultDesktopNavigation } from '../../../mocks/defaultDesktopNavigation.js';
import { useMenuIntersectionObservers } from '../../../hooks/useMenuIntersectionObservers.js';
import { useAnalyticEvents, EVENTS } from '../../../hooks/useAnalyticEvents.js';
import '../../Navigation/index.js';
import '../../InjectedIcon/index.js';
import { MENU_DELAY } from '../../../constants/settings.js';
import { generateInitialMenuState, getVisibleMenuItemsWidth, getHiddenMenuItems } from '../../../utils/navigation.js';
import modules_9a3b202d from '../styles/Header.module.less.js';
import '../../ContentWidget/index.js';
import { showWidgetParam } from '../../../constants/links.js';
import InjectedIcon from '../../InjectedIcon/src/InjectedIcon.js';
import NavigationItem from '../../Navigation/src/NavigationItem.js';
import { ContentWidget } from '../../ContentWidget/src/ContentWidget.js';
import Navigation from '../../Navigation/src/Navigation.js';

/**
 * @description Desktop navigation component.
 *
 * @component
 * @example
 * ```jsx
 * <DesktopNavigation settings={settings} />
 * ```
 */
var DesktopNavigation = React.forwardRef(function (props, ref) {
    var _a = props.settings, settings = _a === void 0 ? {} : _a, promo = props.promo, _b = props.showContentWidget, showContentWidget = _b === void 0 ? false : _b, className = props.className, onOpen = props.onOpen, onClose = props.onClose;
    var moreButtonRef = React.useRef(null);
    var navRef = React.useRef(null);
    var navigation = React.useMemo(function () { return settings.menu || defaultDesktopNavigation; }, [settings]);
    var _c = __read(React.useState(''), 2), activeSection = _c[0], setActiveSection = _c[1];
    var _d = __read(React.useState([]), 2), hiddenMenuItems = _d[0], setHiddenMenuItems = _d[1];
    var _e = __read(React.useState(generateInitialMenuState(navigation)), 2), menuState = _e[0], setMenuState = _e[1];
    var _f = __read(React.useState(0), 2), moreButtonOffset = _f[0], setMoreButtonOffset = _f[1];
    var _g = __read(useBoolean(false), 2), moreButtonDisplayNone = _g[0], setMoreButtonDisplayNone = _g[1];
    var _h = __read(useBoolean(false), 2), moreContentVisible = _h[0], setMoreContentVisible = _h[1];
    var _j = __read(useBoolean(false), 2), widgetVisible = _j[0], setWidgetVisible = _j[1];
    var activeSectionDebounced = useDebounceValue(activeSection, MENU_DELAY);
    var menuStateDebounced = useDebounceValue(menuState, MENU_DELAY);
    var cx = useStyles(modules_9a3b202d);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var moreButtonClassnames = cx('more-button', {
        'more-button--visible': !!hiddenMenuItems.length,
        'more-button--none': moreButtonDisplayNone
    });
    var handleLinkClick = React.useCallback(function (event) {
        var label = event.currentTarget.dataset.label || '';
        pushAnalyticEvent(EVENTS.MENU_LINK_CLICK, { eventLabel: activeSectionDebounced + "|" + label });
    }, [activeSectionDebounced]);
    var handleMoreButtonMenuClick = React.useCallback(function (event) {
        var label = event.currentTarget.dataset.label || '';
        pushAnalyticEvent(EVENTS.MENU_LINK_CLICK, { eventLabel: label + "|\u041F\u0435\u0440\u0435\u0445\u043E\u0434 \u043F\u043E \u0440\u0430\u0437\u0434\u0435\u043B\u0443" });
    }, []);
    var handlePromoBannerClick = React.useCallback(function () {
        pushAnalyticEvent(EVENTS.PROMO_BANNER_CLICK);
    }, []);
    var handlePromoLinkInView = React.useCallback(function (inView, entry) {
        // dataset есть, типизация странная
        var label = entry.target.dataset.label || '';
        var url = entry.target.href || '';
        if (inView && label.length > 0) {
            pushAnalyticEvent(EVENTS.PROMO_LINK_IN_VIEW, { eventLabel: activeSectionDebounced + "|" + label + "|" + url });
        }
    }, [activeSectionDebounced]);
    var handleTopMenuLinksInView = function (inView, entry) {
        var _a;
        var key = (_a = entry.target.firstChild) === null || _a === void 0 ? void 0 : _a.textContent;
        key &&
            setMenuState(function (prevState) {
                var _a;
                return __assign(__assign({}, prevState), (_a = {}, _a[key] = { inView: inView, width: entry.boundingClientRect.width }, _a));
            });
    };
    var showMenu = function (section, hasWidget) {
        setActiveSection(section);
        setMoreContentVisible.off();
        if (hasWidget && !widgetVisible)
            setWidgetVisible.on();
        onOpen();
    };
    var hideMenu = function () {
        setActiveSection('');
        onClose();
    };
    var intersectionObservedRefs = useMenuIntersectionObservers({
        menu: navigation,
        promoLinksCallback: handlePromoLinkInView,
        topMenuLinksCallback: handleTopMenuLinksInView
    }).refs;
    var renderGroups = function (groups, showWidget) {
        var groupsToShow = __spreadArray([], __read(groups));
        if (showWidget && groups.length === 3) {
            groupsToShow.splice(1, 1);
        }
        return groupsToShow.map(function (group) { return (React.createElement(Col, { key: group.text, span: showWidget ? 3 : 4 },
            React.createElement(Space, { size: 20, direction: "vertical", align: "start" }, group.children.map(function (config) { return (React.createElement(Link, { ref: intersectionObservedRefs["" + config.text + config.href], "data-label": config.text, color: "black", key: "" + config.text + config.href, href: config.href, className: cx('subitem'), style: getCustomStyles(config), target: config.openInNew ? '_blank' : undefined, rel: makeRelAttribute(config), onClick: handleLinkClick },
                React.createElement(Space, { align: "center", size: 12, inline: true, className: cx('text-wrapper') },
                    !!config.iconSrc && (React.createElement(Icon, { size: 16, color: "gray", style: getCustomIconStyles(config.href) },
                        React.createElement(InjectedIcon, { iconSrc: config.iconSrc }))),
                    config.icon && !config.iconSrc ? (React.createElement(Icon, { size: 16, color: "gray", style: getCustomIconStyles(config.href) },
                        React.createElement("img", { src: config.icon }))) : null,
                    React.createElement(Text, { as: "span", size: 16, strong: true }, config.text)))); })))); });
    };
    var renderTopLink = function (config) {
        var domains = props.domains;
        var _a = __assign({}, config), text = _a.text, href = _a.href, children = _a.children;
        var showWidget = showContentWidget && widgetVisible && href.includes(showWidgetParam);
        return (React.createElement(NavigationItem, { ref: intersectionObservedRefs["" + text + href], className: cx('nav-item'), key: text, config: config, activeItem: activeSectionDebounced, style: getCustomStyles(config), onMouseEnter: function () { return showMenu(text, href.includes(showWidgetParam)); }, onMouseLeave: hideMenu },
            React.createElement(Row, { gutter: 20 },
                renderGroups(children, showWidget),
                showWidget && (React.createElement(Col, { span: 6 },
                    React.createElement(ContentWidget, { domain: domains.base }))))));
    };
    var moreItemContent = React.useMemo(function () { return (React.createElement(Menu, null, hiddenMenuItems.map(function (menuItem) { return (React.createElement(Link, { "data-label": menuItem.text, color: "black", key: menuItem.text, href: menuItem.href, style: getCustomStyles(menuItem), target: menuItem.openInNew ? '_blank' : undefined, rel: makeRelAttribute(menuItem), onClick: handleMoreButtonMenuClick },
        React.createElement(Menu.Item, { className: cx('more-item') },
            React.createElement(Text, { as: "span", size: 16, strong: true }, menuItem.text)))); }))); }, [hiddenMenuItems]);
    var menu = React.useMemo(function () { return navigation.map(renderTopLink); }, [navigation, activeSectionDebounced]);
    var getNavigationWidth = function () { return navRef.current ? navRef.current.getBoundingClientRect().width : 0; };
    React.useEffect(function () {
        !!activeSectionDebounced && pushAnalyticEvent(EVENTS.TOP_MENU_SHOW, { eventLabel: activeSectionDebounced + "|\u041D\u0430\u0432\u0435\u0434\u0435\u043D\u0438\u0435" });
    }, [activeSectionDebounced]);
    React.useEffect(function () {
        var navigationWidth = getNavigationWidth();
        var visibleItemsWidth = getVisibleMenuItemsWidth(menuStateDebounced);
        if (visibleItemsWidth) {
            var hiddenItems = getHiddenMenuItems(navigation, menuStateDebounced);
            setMoreButtonOffset(0 - navigationWidth + visibleItemsWidth);
            setHiddenMenuItems(hiddenItems);
        }
    }, [menuStateDebounced, navigation]);
    React.useEffect(function () {
        if (hiddenMenuItems.length === 1) {
            var navigationWidth = getNavigationWidth();
            // TODO исправить после фикса ref в Popover ДС
            //const { width: moreButtonWidth } = moreButtonRef.current ? moreButtonRef.current.getBoundingClientRect() : { width: 0 }
            var moreButtonWidth = 53;
            var visibleItemsWidth = getVisibleMenuItemsWidth(menuStateDebounced);
            var lastHiddenMenuItem = navigation[navigation.length - 1];
            var fitLastMenuItem = navigationWidth + moreButtonWidth - visibleItemsWidth - menuStateDebounced[lastHiddenMenuItem.text].width >= 0;
            fitLastMenuItem && setMoreButtonDisplayNone.on();
        }
        if (hiddenMenuItems.length > 1) {
            setMoreButtonDisplayNone.off();
        }
    }, [hiddenMenuItems, menuStateDebounced, navigation]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Navigation, { ref: useMergedRef(ref, navRef), className: className }, menu),
        React.createElement(Popover, { visible: moreContentVisible, content: moreItemContent, onClose: setMoreContentVisible.off },
            React.createElement("div", { ref: moreButtonRef, className: moreButtonClassnames, style: { left: moreButtonOffset + "px" }, onClick: setMoreContentVisible.toggle },
                React.createElement(Text, { strong: true }, "\u0415\u0449\u0451"))),
        promo && (React.createElement("div", { className: "h-ml-16 h-mr-16", onClickCapture: handlePromoBannerClick }, promo))));
});
DesktopNavigation.defaultProps = {};

export { DesktopNavigation as default };
//# sourceMappingURL=DesktopNavigation.js.map
